import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import B2BHubHomeHeader from '../../components/B2BHubHomeHeader';
import HubFloatingRail from '../../components/hub/HubFloatingRail';
import ExploreInfraListingsBrowse from '../../components/marketplace/ExploreInfraListingsBrowse';
import { readParam } from '../../components/marketplace/MarketplaceBrowseUi';
import { useMasterFacilityTypes } from '../../hooks/useMasterFacilityTypes';
import { useMasterFacilitySubTypes } from '../../hooks/useMasterFacilitySubTypes';
import { useMasterFacilitySubSubTypes } from '../../hooks/useMasterFacilitySubSubTypes';
import { fetchPublishedInfrastructureListings } from '../../services/infrastructureLeasingListing.service';
import b2bHubHomeService from '../../services/b2bHubHome.service';
import CommonService from '../../services/common.service';
import {
  ILE_AVAILABILITY_STATES,
  ILE_CITY_SUGGESTIONS,
  ILE_OWNERSHIP_TYPES,
  ILE_PROPERTY_CATEGORIES,
  ILE_TRANSACTION_TYPES,
  listingAvailabilityFilterKey,
  normalizeListingOwnershipType,
} from '../../utils/infrastructureLeasingSearchConfig';
import { readSearchQueryFromParams } from '../../config/globalHeaderSearch';
import {
  findMatchingCategoryIds,
  findMatchingSubCategoryIds,
  findMatchingSubSubCategoryIds,
  matchesListingSearchQuery,
} from '../../utils/listingSearchMatch';
import '../../assets/styles/home.scss';
import '../../assets/styles/product_marketplace.scss';
import '../../assets/styles/explore_product_categories.scss';
import '../../assets/styles/infrastructure_search_listings.scss';

const forListingType = (item, type) => {
  if (type === 'property') {
    return item.listingType === 'property' || item.listingType === 'unknown';
  }
  if (type === 'machinery') {
    return item.listingType === 'machinery';
  }
  return true;
};

const isNewListing = (updatedAt) => {
  if (!updatedAt) return false;
  return Date.now() - new Date(updatedAt).getTime() < 7 * 24 * 60 * 60 * 1000;
};

const BUDGET_SLIDER_MAX_LAKH = 1000;
const BUDGET_SLIDER_STEP_LAKH = 0.1;

function formatBudgetLakhLabel(lakhs) {
  return `₹${Number(lakhs).toFixed(1)}L`;
}

function lakhToRupees(lakhs) {
  return Math.round(Number(lakhs) * 100000);
}

export default function InfrastructureLeasingSearchListingsPage() {
  const [searchParams] = useSearchParams();

  const browsePath = useMemo(
    () => ({
      type: readParam(searchParams, 'type'),
      assetCategory: readParam(searchParams, 'assetCategory'),
      subCategory: readParam(searchParams, 'subCategory'),
      subSubCategory: readParam(searchParams, 'subSubCategory'),
    }),
    [searchParams],
  );

  const hasBrowsePath = Boolean(
    browsePath.type || browsePath.assetCategory || browsePath.subCategory || browsePath.subSubCategory,
  );
  const listingType =
    browsePath.type === 'property' || browsePath.type === 'machinery' ? browsePath.type : null;
  const legacyTab = searchParams.get('tab') === 'equipment' ? 'equipment' : 'real-estate';
  const effectiveType =
    listingType || (legacyTab === 'equipment' ? 'machinery' : hasBrowsePath ? null : 'property');

  const { list: facilityTypes } = useMasterFacilityTypes(listingType || browsePath.type || null);
  const { list: allFacilityTypes } = useMasterFacilityTypes(null);
  const { list: facilitySubTypes } = useMasterFacilitySubTypes(browsePath.assetCategory);
  const { list: facilitySubSubTypes } = useMasterFacilitySubSubTypes(browsePath.subCategory);

  const activeAssetCategory = facilityTypes.find(
    (r) => String(r.facility_type_id) === String(browsePath.assetCategory),
  );
  const activeSubCategory = facilitySubTypes.find(
    (r) => String(r.facility_sub_type_id) === String(browsePath.subCategory),
  );
  const activeSubSubCategory = facilitySubSubTypes.find(
    (r) => String(r.facility_sub_sub_type_id) === String(browsePath.subSubCategory),
  );

  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketplaceBanners, setMarketplaceBanners] = useState([]);
  const urlQuery = readSearchQueryFromParams(searchParams);
  const [q, setQ] = useState(urlQuery);
  const [sort, setSort] = useState('newest');
  const [trans, setTrans] = useState(() => new Set());
  const [cities, setCities] = useState(() => new Set());
  const [states, setStates] = useState(() => new Set());
  const [ownership, setOwnership] = useState(() => new Set());
  const [availability, setAvailability] = useState(() => new Set());
  const [propertyTypes, setPropertyTypes] = useState(() => new Set());
  const [onlyNew, setOnlyNew] = useState(false);
  const [budgetMinLakh, setBudgetMinLakh] = useState(0);
  const [budgetMaxLakh, setBudgetMaxLakh] = useState(BUDGET_SLIDER_MAX_LAKH);
  const [facilitySubTypeIndex, setFacilitySubTypeIndex] = useState(() => new Map());
  const [facilitySubSubTypeIndex, setFacilitySubSubTypeIndex] = useState(() => new Map());

  const facilityTypeNameById = useMemo(() => {
    const map = new Map();
    allFacilityTypes.forEach((row) => {
      map.set(String(row.facility_type_id), row.facility_type);
    });
    return map;
  }, [allFacilityTypes]);

  useEffect(() => {
    setQ(readSearchQueryFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    b2bHubHomeService
      .getProductCategories()
      .then((res) => {
        if (!cancelled) {
          const payload = res?.data ?? res ?? {};
          setMarketplaceBanners(payload.marketplace_banners || []);
        }
      })
      .catch(() => {
        if (!cancelled) setMarketplaceBanners([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!urlQuery.trim() || !allFacilityTypes.length) {
      setFacilitySubTypeIndex(new Map());
      setFacilitySubSubTypeIndex(new Map());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const subMap = new Map();
      const subSubMap = new Map();
      await Promise.all(
        allFacilityTypes.map(async (facilityType) => {
          const typeId = facilityType.facility_type_id;
          try {
            const subRes = await CommonService.get(
              `/master/facility-sub-types/${typeId}`,
              {},
              { silent: true },
            );
            const subs = Array.isArray(subRes?.data) ? subRes.data : [];
            await Promise.all(
              subs.map(async (sub) => {
                subMap.set(String(sub.facility_sub_type_id), sub.facility_sub_type);
                try {
                  const subSubRes = await CommonService.get(
                    `/master/facility-sub-sub-types/${sub.facility_sub_type_id}`,
                    {},
                    { silent: true },
                  );
                  const subSubs = Array.isArray(subSubRes?.data) ? subSubRes.data : [];
                  subSubs.forEach((subSub) => {
                    subSubMap.set(String(subSub.facility_sub_sub_type_id), subSub.facility_sub_sub_type);
                  });
                } catch {
                  /* ignore */
                }
              }),
            );
          } catch {
            /* ignore */
          }
        }),
      );
      if (!cancelled) {
        setFacilitySubTypeIndex(subMap);
        setFacilitySubSubTypeIndex(subSubMap);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [urlQuery, allFacilityTypes]);

  useEffect(() => {
    let c = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchPublishedInfrastructureListings(200);
        if (!c && res?.success && Array.isArray(res?.data?.items)) {
          setAllItems(res.data.items);
        }
      } catch {
        if (!c) setAllItems([]);
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  const toggle = (setFn, v) => {
    setFn((prev) => {
      const n = new Set(prev);
      if (n.has(v)) n.delete(v);
      else n.add(v);
      return n;
    });
  };

  const matchesBrowsePath = (item) => {
    if (browsePath.type && !forListingType(item, browsePath.type)) return false;
    if (browsePath.assetCategory && String(item.assetCategoryId) !== String(browsePath.assetCategory)) return false;
    if (browsePath.subCategory && String(item.subCategoryId) !== String(browsePath.subCategory)) return false;
    if (browsePath.subSubCategory && String(item.subSubCategoryId) !== String(browsePath.subSubCategory)) return false;
    return true;
  };

  const inScope = useMemo(() => {
    let items = allItems || [];
    if (effectiveType) {
      items = items.filter((p) => forListingType(p, effectiveType));
    }
    if (hasBrowsePath) {
      items = items.filter(matchesBrowsePath);
    }
    return items;
  }, [allItems, effectiveType, hasBrowsePath, browsePath]);

  const cityOptions = useMemo(() => {
    const set = new Set();
    inScope.forEach((item) => {
      const city = item.cityName || item.locationLine;
      if (city && city !== '—') set.add(String(city).trim());
    });
    ILE_CITY_SUGGESTIONS.forEach((c) => set.add(c));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inScope]);

  const txOptions = useMemo(() => {
    const set = new Set();
    inScope.forEach((item) => {
      set.add((item.transactionType || 'lease').toLowerCase());
    });
    return ILE_TRANSACTION_TYPES.filter((t) => set.has(t.value));
  }, [inScope]);

  const stateOptions = useMemo(() => {
    const set = new Set();
    inScope.forEach((item) => {
      const state = item.stateName && item.stateName !== '—' ? String(item.stateName).trim() : '';
      if (state) set.add(state);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inScope]);

  const ownershipOptions = useMemo(() => {
    const set = new Set();
    inScope.forEach((item) => {
      const key = normalizeListingOwnershipType(item.ownershipType);
      if (key !== 'other') set.add(key);
    });
    return ILE_OWNERSHIP_TYPES.filter((t) => set.has(t.value));
  }, [inScope]);

  const availabilityOptions = useMemo(() => {
    const set = new Set();
    inScope.forEach((item) => {
      set.add(listingAvailabilityFilterKey(item));
    });
    return ILE_AVAILABILITY_STATES.filter((t) => set.has(t.value));
  }, [inScope]);

  const propertyTypeOptions = useMemo(() => {
    if (effectiveType !== 'property' || browsePath.subCategory) return [];
    const set = new Set();
    inScope.forEach((item) => {
      if (item.propertyType) set.add(String(item.propertyType));
    });
    return ILE_PROPERTY_CATEGORIES.filter((t) => set.has(t.value));
  }, [inScope, effectiveType, browsePath.subCategory]);

  const showPropertyTypeFilter = propertyTypeOptions.length > 1;

  const budgetFillStyle = useMemo(() => {
    const left = (budgetMinLakh / BUDGET_SLIDER_MAX_LAKH) * 100;
    const right = 100 - (budgetMaxLakh / BUDGET_SLIDER_MAX_LAKH) * 100;
    return { left: `${left}%`, right: `${right}%` };
  }, [budgetMinLakh, budgetMaxLakh]);

  const matchesCity = (item) => {
    if (!cities.size) return true;
    const loc = [item.locationLine, item.cityName, item.stateName].filter(Boolean).join(' ').toLowerCase();
    for (const city of cities) {
      if (loc.includes(city.toLowerCase())) return true;
    }
    return false;
  };

  const matchesState = (item) => {
    if (!states.size) return true;
    const state = item.stateName && item.stateName !== '—' ? String(item.stateName).trim() : '';
    if (state && states.has(state)) return true;
    const loc = [item.locationLine, item.cityName].filter(Boolean).join(' ').toLowerCase();
    for (const name of states) {
      if (loc.includes(String(name).toLowerCase())) return true;
    }
    return false;
  };

  const matchesOwnership = (item) => {
    if (!ownership.size) return true;
    return ownership.has(normalizeListingOwnershipType(item.ownershipType));
  };

  const matchesAvailability = (item) => {
    if (!availability.size) return true;
    return availability.has(listingAvailabilityFilterKey(item));
  };

  const matchesPropertyType = (item) => {
    if (!propertyTypes.size) return true;
    return propertyTypes.has(String(item.propertyType || ''));
  };

  const matchesTx = (item) => {
    if (!trans.size) return true;
    return trans.has((item.transactionType || 'lease').toLowerCase());
  };

  const matchesBudget = (item) => {
    const atFullRange = budgetMinLakh <= 0 && budgetMaxLakh >= BUDGET_SLIDER_MAX_LAKH;
    if (atFullRange) return true;

    const min = lakhToRupees(budgetMinLakh);
    const max = lakhToRupees(budgetMaxLakh);
    const n = item.rentAmount;
    if (n == null || !Number.isFinite(n)) return false;
    if (n < min) return false;
    if (n > max) return false;
    return true;
  };

  const searchFieldsForItem = (item) => [
    item.title,
    item.description,
    item.locationLine,
    item.categoryLabel,
    item.keySpec,
    item.price,
    item.pricePrimary,
    item.propertyType,
    item.subCategory,
    item.cityName,
    item.stateName,
    facilityTypeNameById.get(String(item.assetCategoryId)),
    facilitySubTypeIndex.get(String(item.subCategoryId)),
    facilitySubSubTypeIndex.get(String(item.subSubCategoryId)),
  ];

  const matchingAssetCategoryIds = useMemo(
    () =>
      findMatchingCategoryIds(
        q,
        allFacilityTypes.map((row) => ({
          id: row.facility_type_id,
          name: row.facility_type,
        })),
      ),
    [q, allFacilityTypes],
  );

  const matchingFacilitySubCategoryIds = useMemo(
    () =>
      findMatchingSubCategoryIds(
        q,
        [...facilitySubTypeIndex.entries()].map(([id, name]) => ({ id, name })),
      ),
    [q, facilitySubTypeIndex],
  );

  const matchingFacilitySubSubCategoryIds = useMemo(
    () =>
      findMatchingSubSubCategoryIds(
        q,
        [...facilitySubSubTypeIndex.entries()].map(([id, name]) => ({ id, name })),
      ),
    [q, facilitySubSubTypeIndex],
  );

  const filtered = useMemo(() => {
    let r = inScope.filter(
      (it) =>
        matchesCity(it) &&
        matchesState(it) &&
        matchesTx(it) &&
        matchesBudget(it) &&
        matchesOwnership(it) &&
        matchesAvailability(it) &&
        matchesPropertyType(it) &&
        (!onlyNew || isNewListing(it.updatedAt)),
    );
    const qt = q.trim();
    if (qt) {
      r = r.filter(
        (item) =>
          matchesListingSearchQuery(qt, searchFieldsForItem(item)) ||
          matchingAssetCategoryIds.has(String(item.assetCategoryId)) ||
          matchingFacilitySubCategoryIds.has(String(item.subCategoryId)) ||
          matchingFacilitySubSubCategoryIds.has(String(item.subSubCategoryId)),
      );
    }
    r = [...r];
    if (sort === 'newest') {
      r.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else if (sort === 'price-asc') {
      r.sort((a, b) => (a.rentAmount ?? 1e15) - (b.rentAmount ?? 1e15));
    } else if (sort === 'price-desc') {
      r.sort((a, b) => (b.rentAmount ?? -1) - (a.rentAmount ?? -1));
    }
    return r;
  }, [
    inScope,
    q,
    sort,
    trans,
    cities,
    states,
    ownership,
    availability,
    propertyTypes,
    onlyNew,
    budgetMinLakh,
    budgetMaxLakh,
    facilityTypeNameById,
    facilitySubTypeIndex,
    facilitySubSubTypeIndex,
    matchingAssetCategoryIds,
    matchingFacilitySubCategoryIds,
    matchingFacilitySubSubCategoryIds,
  ]);

  const clearFilters = () => {
    setQ('');
    setSort('newest');
    setTrans(new Set());
    setCities(new Set());
    setStates(new Set());
    setOwnership(new Set());
    setAvailability(new Set());
    setPropertyTypes(new Set());
    setOnlyNew(false);
    setBudgetMinLakh(0);
    setBudgetMaxLakh(BUDGET_SLIDER_MAX_LAKH);
  };

  const typeLabel =
    browsePath.type === 'machinery' ? 'Machinery / Asset' : browsePath.type === 'property' ? 'Property' : null;

  const pageTitle =
    activeSubSubCategory?.facility_sub_sub_type ||
    activeSubCategory?.facility_sub_type ||
    activeAssetCategory?.facility_type ||
    (typeLabel ? `${typeLabel} Listings` : 'Infrastructure Listings');

  const heroBreadcrumbTrail = [
    typeLabel,
    activeAssetCategory?.facility_type,
    activeSubCategory?.facility_sub_type,
    activeSubSubCategory?.facility_sub_sub_type,
  ].filter(Boolean);

  const pageDescription =
    hasBrowsePath && heroBreadcrumbTrail.length
      ? `Browse published infrastructure listings in ${heroBreadcrumbTrail.join(' › ')}. Filter by transaction type, location, budget, and availability.`
      : 'Browse published property and machinery infrastructure listings from verified lessors and asset owners.';

  const detailTab = browsePath.type === 'machinery' || legacyTab === 'equipment' ? 'equipment' : 'real-estate';

  const handleFilterToggle = (key, value) => {
    const setters = {
      trans: setTrans,
      availability: setAvailability,
      ownership: setOwnership,
      propertyTypes: setPropertyTypes,
      states: setStates,
      cities: setCities,
    };
    if (setters[key]) toggle(setters[key], value);
  };

  const filterPanelProps = {
    txOptions,
    availabilityOptions,
    ownershipOptions,
    propertyTypeOptions,
    showPropertyTypeFilter,
    stateOptions,
    cityOptions,
    trans,
    availability,
    ownership,
    propertyTypes,
    states,
    cities,
    onlyNew,
    budgetMinLakh,
    budgetMaxLakh,
    budgetFillStyle,
    budgetSliderMaxLakh: BUDGET_SLIDER_MAX_LAKH,
    budgetSliderStepLakh: BUDGET_SLIDER_STEP_LAKH,
    formatBudgetLakhLabel,
    onToggle: handleFilterToggle,
    onOnlyNewChange: setOnlyNew,
    onBudgetMinChange: (value) => setBudgetMinLakh(Math.min(value, budgetMaxLakh)),
    onBudgetMaxChange: (value) => setBudgetMaxLakh(Math.max(value, budgetMinLakh)),
    onClear: clearFilters,
  };

  return (
    <div className="product-marketplace-page product-marketplace-page--explore product-marketplace-page--subcategory-products">
      <B2BHubHomeHeader />
      <ExploreInfraListingsBrowse
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        browsePath={browsePath}
        breadcrumbTrail={heroBreadcrumbTrail}
        listings={filtered}
        loading={loading}
        searchValue={q}
        onSearchChange={setQ}
        sort={sort}
        onSortChange={setSort}
        detailTab={detailTab}
        filterPanelProps={filterPanelProps}
        advertisementBanners={marketplaceBanners}
      />
      <HubFloatingRail />
    </div>
  );
}
